/* eslint-disable parog-web/no-glint-expect-error-directive */
import type {
  TemplateOnlyComponent,
  TOC
} from '@ember/component/template-only';
import { on } from '@ember/modifier';
import dayjs from 'dayjs';
import { validateDate } from 'ember-changeset-validations/validators';
import changesetGet from 'ember-changeset/helpers/changeset-get';
import type { ValidatorMap } from 'ember-changeset/types';
import { and, eq, not } from 'ember-truth-helpers';
import WrButton from 'ember-wiser/components/button';
import type {
  ForcModelPojo,
  Payload as ForcPayload
} from 'parog-web/components/forc/abstract/journal-event-based-component';
import type { Signature as ForcProviderBaseSignature } from 'parog-web/components/forc/provider/base';
import DeseaseHistoryComponent from 'parog-web/components/lgc/desease-history/desease-history-component';
import MaritalStatusDropdown from 'parog-web/components/lifestyle/edition/marital-status-dropdown';
import BaseMedicalHistoryFormComponent from 'parog-web/components/medical-history/abstract/base-edition-form';
import AccordionBlock from 'parog-web/components/medical-history/accordion-block';
import ModelList from 'parog-web/components/model-list';
import TreatmentFormModal from 'parog-web/components/treatment/treatment-form-modal';
import { dateYearFormat } from 'parog-web/helpers/datepicker-format';
import type Patient from 'parog-web/models/patient';
import Vaccine from 'parog-web/models/vaccine';
import {
  articularOptions,
  ophtalmicSymptomsOptions,
  oralLesionsOptions,
  skinLesionsOptions
} from 'parog-web/utils/lgc-form-options';
import MedicalHistoryBaseEditionForm from './base-edition-form';
import DigestCrohnDiagnostic from './digest/crohn/diagnostic-fields';
import DigestRchDiagnostic from './digest/rch/diagnostic-fields';
import Surgeries from './digest/surgeries';
interface Formatter {
  date: (date: Date) => string;
}

const DiagnosticFields = {
  crohn: DigestCrohnDiagnostic,
  rch: DigestRchDiagnostic
};

const History = <template>
  {{#if @patient.healthCentre.lgcEnabled}}
    <AccordionBlock @title='Histoire de la maladie' @isActive={{true}}>
      <DeseaseHistoryComponent @patient={{@patient}} />
    </AccordionBlock>
  {{else}}
    <AccordionBlock @title='Antécédents' @isActive={{true}}>
      <div class='field'>
        <@form.Input
          @labelName='Histoire de la maladie'
          @propertyName='history'
          @placeholder='Texte libre'
          @dataTestInput='history'
        />
      </div>
    </AccordionBlock>
  {{/if}}
</template> satisfies TOC<{
  Args: {
    form: ForcProviderBaseSignature['Blocks']['default'][0];
    patient: Patient;
  };
}>;

const CurrentTreatments = <template>
  <AccordionBlock @title='Traitements en cours' @isActive={{true}}>
    <TreatmentFormModal @patient={{@patient}} as |displayFormTable|>
      <WrButton
        @variant='outlined'
        class='mb-4 w-full'
        {{on 'click' displayFormTable}}
      >
        <i class='eye icon fitted outline'></i>
        Traitements MICI
      </WrButton>
    </TreatmentFormModal>

    <div class='field'>
      <@form.Input
        @labelName='Traitements non-MICI'
        @propertyName='nonMiciTreatmentsComment'
        @placeholder='Texte libre'
      />
    </div>
  </AccordionBlock>
</template> satisfies TOC<{
  Args: {
    form: ForcProviderBaseSignature['Blocks']['default'][0];
    patient: Patient;
  };
}>;

const OtherPersonalHistory = <template>
  <AccordionBlock @title='Autres antécédents personnels' @isActive={{true}}>
    <div class='field'>
      <@form.Input
        @labelName='Infectieux'
        @propertyName='infectious'
        @placeholder='Texte libre'
        @dataTestInput='infectious'
      />
    </div>

    <div class='field'>
      <@form.Input
        @labelName='Auto-immun, inflammatoire'
        @propertyName='autoImmuneInflammatory'
        @placeholder='Texte libre'
        @dataTestInput='autoImmuneInflammatory'
      />
    </div>

    <div class='field'>
      <@form.Input
        @labelName='Cardiologique'
        @propertyName='cardiovascular'
        @placeholder='Texte libre'
        @dataTestInput='cardiovascular'
      />
    </div>

    <div class='field'>
      <@form.Input
        @labelName='Gynéco-obstétrique'
        @propertyName='gynecoObstetric'
        @placeholder='Texte libre'
        @dataTestInput='gynecoObstetric'
      />
    </div>

    <div class='field'>
      <@form.Input
        @labelName='Cancérologique'
        @propertyName='oncological'
        @placeholder='Texte libre'
        @dataTestInput='oncological'
      />
    </div>

    <div class='field'>
      <@form.Input
        @labelName='Autre'
        @propertyName='otherPersonal'
        @placeholder='Texte libre'
        @dataTestInput='otherPersonal'
      />
    </div>
  </AccordionBlock>
</template> satisfies TOC<{
  Args: {
    form: ForcProviderBaseSignature['Blocks']['default'][0];
  };
}>;

const FamilyHistory = <template>
  <AccordionBlock @title='Antécédents familiaux' @isActive={{true}}>
    <div class='field'>
      <@form.Input
        @labelName='MICI'
        @propertyName='familyMici'
        @placeholder='Texte libre'
        @dataTestInput='familyMici'
      />
    </div>

    <div class='field'>
      <@form.Input
        @labelName='Autre'
        @propertyName='familyOther'
        @placeholder='Texte libre'
        @dataTestInput='familyOther'
      />
    </div>
  </AccordionBlock>
</template> satisfies TOC<{
  Args: {
    form: ForcProviderBaseSignature['Blocks']['default'][0];
  };
}>;

const LifeStyle = <template>
  <AccordionBlock @title='Mode de vie' @isActive={{true}}>
    <div class='field'>
      <@form.Input
        @labelName='Activité (Profession)'
        @propertyName='activity'
        @placeholder='Texte libre'
        @dataTestInput='activity'
      />
    </div>

    <div class='field'>
      <MaritalStatusDropdown @changeset={{@form.changeset}} />
    </div>

    <div class='field'>
      <@form.NumberInput
        @labelName='Enfants (nombres)'
        @propertyName='numberOfChildren'
        @min='0'
      />
    </div>

    <div class='field'>
      <@form.Input
        @labelName='Loisirs et activités physiques'
        @propertyName='hobbies'
      />
    </div>

    <div class='field'>
      <@form.Input @labelName='Aide à domicile' @propertyName='homeHelp' />
    </div>

    <div class='field'>
      <@form.Dropdown
        @labelName='Tabac'
        @propertyName='smokerDetails'
        @translationKey='medical-history-changed-journal-event.smoker-details'
        as |dropdown|
      >
        <dropdown.Item @key='none' />
        <dropdown.Item @key='none-active' />
        <dropdown.Item @key='active' />
        <dropdown.Item @key='old' />
        <dropdown.Item @key='e-cigarette' />
      </@form.Dropdown>
    </div>

    {{#if
      (and
        (changesetGet @form.changeset 'smokerDetails')
        (not (eq (changesetGet @form.changeset 'smokerDetails') 'none'))
      )
    }}
      <div class='field'>
        <@form.NumberInput
          @labelName='Nombre de cigarettes par jour'
          @propertyName='dailyCigarettesNumber'
          @min='0'
          @type='integer'
        />
      </div>
      <div class='field'>
        <@form.Date
          @propertyName='smokingStartDate'
          @labelName='Année de début'
          @formatter={{@dateFormatter}}
        />
      </div>
      <div class='field'>
        <@form.Date
          @propertyName='smokingEndDate'
          @labelName='Année de fin'
          @formatter={{@dateFormatter}}
        />
      </div>
    {{/if}}

    <div class='field'>
      <@form.Dropdown
        @labelName='Alcool'
        @propertyName='alcohol'
        @translationKey='medical-history-changed-journal-event.alcohol'
        as |dropdown|
      >
        <dropdown.Item @key='never' />
        <dropdown.Item @key='sometimes' />
        <dropdown.Item @key='often' />
        <dropdown.Item @key='everyday' />
      </@form.Dropdown>
    </div>

    <div class='field'>
      <@form.AlignedFieldsRadioGroup
        @title='Contraception'
        @propertyName='contraception'
        as |Group|
      >
        <Group.FieldRadio @label='Oui' @value={{true}} />
        <Group.FieldRadio @label='Non' @value={{false}} />
      </@form.AlignedFieldsRadioGroup>
    </div>

    <div class='field'>
      <@form.Dropdown
        @labelName='Habitudes alimentaires prédominantes'
        @propertyName='predominantEatingHabit'
        @translationKey='medical-history-changed-journal-event.predominant-eating-habit'
        as |dropdown|
      >
        <dropdown.Item @key='normal' />
        <dropdown.Item @key='healthy_diet' />
        <dropdown.Item @key='unhealthy_diet' />
        <dropdown.Item @key='low_fodmap' />
        <dropdown.Item @key='gluten_free' />
        <dropdown.Item @key='lactose_free' />
        <dropdown.Item @key='residue_free' />
        <dropdown.Item @key='intermittent_fasting' />
        <dropdown.Item @key='high_protein' />
        <dropdown.Item @key='vegetarian' />
      </@form.Dropdown>
    </div>

    <div class='field'>
      <@form.Input
        @labelName='Autres habitudes alimentaires'
        @propertyName='usualEatingHabit'
      />
    </div>

    <div class='field'>
      <@form.Input @labelName='Autres' @propertyName='lifestyleComment' />
    </div>
  </AccordionBlock>
</template> satisfies TOC<{
  Args: {
    form: ForcProviderBaseSignature['Blocks']['default'][0];
    dateFormatter: Formatter;
  };
}>;

const SurgeriesHistory = <template>
  <AccordionBlock
    data-test-accordion='surgeries'
    @title='Antécédents chirurgicaux'
    @isActive={{true}}
  >

    <ModelList
      @model={{@form.changeset}}
      @propertyName='digestiveSurgery'
      @addButtonTitle='Ajouter une chirurgie digestive'
      as |Item|
    >
      <Surgeries
        @title='Chirurgie digestive'
        @model={{Item.item}}
        @index={{Item.index}}
        @onDestroy={{Item.destroy}}
        @onDirty={{Item.setDirty}}
      />
    </ModelList>

    <ModelList
      @model={{@form.changeset}}
      @propertyName='proctologicalSurgery'
      @addButtonTitle='Ajouter une chirurgie proctologique'
      @listClass='mt-4'
      as |Item|
    >
      <Surgeries
        @title='Chirurgie proctologique'
        @model={{Item.item}}
        @index={{Item.index}}
        @onDestroy={{Item.destroy}}
        @onDirty={{Item.setDirty}}
      />
    </ModelList>
  </AccordionBlock>
</template> satisfies TOC<{
  Args: {
    form: ForcProviderBaseSignature['Blocks']['default'][0];
  };
}>;

const ExtraIntestinalSection = <template>
  <AccordionBlock @title='Manifestations extra intestinales' @isActive={{true}}>
    <div class='field'>
      <@form.Dropdown
        @multipleSelection={{true}}
        @labelName='Articulaires'
        @propertyName='articular'
        as |dropdown|
      >
        {{#each articularOptions as |option|}}
          <dropdown.Item @key={{option}} @value={{option}} />
        {{/each}}
      </@form.Dropdown>
    </div>
    <div class='field'>
      <@form.Dropdown
        @labelName='Lésions cutanées'
        @propertyName='skinLesions'
        as |dropdown|
      >
        {{#each skinLesionsOptions as |option|}}
          <dropdown.Item @key={{option}} @value={{option}} />
        {{/each}}
      </@form.Dropdown>
    </div>
    <div class='field'>
      <@form.Dropdown
        @labelName='Lésions buccales'
        @propertyName='oralLesions'
        as |dropdown|
      >
        {{#each oralLesionsOptions as |option|}}
          <dropdown.Item @key={{option}} @value={{option}} />
        {{/each}}
      </@form.Dropdown>
    </div>
    <div class='field'>
      <@form.Dropdown
        @labelName='Manifestations ophtalmiques'
        @propertyName='ophtalmicSymptoms'
        as |dropdown|
      >
        {{#each ophtalmicSymptomsOptions as |option|}}
          <dropdown.Item @key={{option}} @value={{option}} />
        {{/each}}
      </@form.Dropdown>
    </div>

    <div class='field'>
      <@form.AlignedFieldsRadioGroup
        @title='Cholangite sclérosante primitive'
        @propertyName='sclerosingCholangitis'
        as |Group|
      >
        <Group.FieldRadio @label='Oui' @value={{true}} />
        <Group.FieldRadio @label='Non' @value={{false}} />
      </@form.AlignedFieldsRadioGroup>
    </div>

    <div class='field'>
      <@form.Input
        @labelName='Autres manifestations'
        @propertyName='otherSymptoms'
        @type='text'
        @placeholder='Texte libre'
      />
    </div>

  </AccordionBlock>
</template> satisfies TOC<{
  Args: {
    form: ForcProviderBaseSignature['Blocks']['default'][0];
  };
}>;

export default class DigestMedicalHistoryFormComponent extends BaseMedicalHistoryFormComponent {
  formatter = {
    date: dateYearFormat
  };
  override journalEventProperties = [
    ...this.sharedProperties,
    ...this.pathologyProperties
  ];
  vaccines = this.changeset.vaccines as unknown as Vaccine[];

  get diagnostic(): TemplateOnlyComponent {
    return DiagnosticFields[this.patho ?? ''];
  }

  get pathologyProperties(): string[] {
    return [
      'activity',
      'alcohol',
      'autoImmuneInflammatory',
      'cardiovascular',
      'contraception',
      'cumulativeSpreadDisease',
      'dailyCigarettesNumber',
      'diagnosisDate',
      'diagnosticAge',
      'diagnosticType',
      'digestiveSurgery',
      'diseaseActivity',
      'evolution',
      'familyMici',
      'familyOther',
      'gynecoObstetric',
      'history',
      'hobbies',
      'homeHelp',
      'infectious',
      'lifestyleComment',
      'localisation',
      'maritalStatus',
      'numberOfChildren',
      'oncological',
      'otherPersonal',
      'predominantEatingHabit',
      'proctologicalSurgery',
      'smokerDetails',
      'smokingEndDate',
      'smokingStartDate',
      'usualEatingHabit',
      'smokerDetails',
      'infectious',
      'autoImmuneInflammatory',
      'cardiovascular',
      'gynecoObstetric',
      'oncological',
      'otherPersonal',
      'familyMici',
      'familyOther',
      'articular',
      'skinLesions',
      'oralLesions',
      'ophtalmicSymptoms',
      'sclerosingCholangitis',
      'otherSymptoms',
      'nonMiciTreatmentsComment'
    ];
  }

  override get validations(): ValidatorMap {
    return {
      diagnosisDate: [
        validateDate({
          allowBlank: true,
          message:
            'Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date',
          onOrAfter: new Date('01/01/1900')
        }),
        validateDate({
          allowBlank: true,
          message:
            'Cette donnée ne peut être déclarée dans le futur. Veuillez modifier la date',
          onOrBefore: dayjs().endOf('day').toDate()
        })
      ]
    };
  }

  override computePayload(): ForcPayload {
    const payload = super.computePayload();

    if (!this.args.patient.isRch) {
      delete payload.diagnosticType;
    }

    return payload;
  }

  override retrieveModel(): ForcModelPojo {
    const model = super.retrieveModel();

    model.vaccines = [
      'seasonalFlu',
      'sarsCov2',
      'dtPolio',
      'ror',
      'meningitis',
      'hpv',
      'yellowFever',
      'hepatitisB',
      'zona',
      'pneumococcus'
    ].map(e => new Vaccine(e));

    model.digestiveSurgery ??= [];
    model.proctologicalSurgery ??= [];

    return model;
  }

  <template>
    <MedicalHistoryBaseEditionForm
      {{! @glint-expect-error }}
      @forcComponent={{this}}
      @patient={{@patient}}
      as |mh|
    >
      <mh.FurSection />
      <History @form={{mh.form}} @patient={{@patient}} />
      <CurrentTreatments @form={{mh.form}} @patient={{@patient}} />
      <mh.FurDetailsSection
        @title='Caractéristiques de la maladie'
        @diagnostic={{this.diagnostic}}
      />

      <SurgeriesHistory @form={{mh.form}} />
      <ExtraIntestinalSection @form={{mh.form}} />
      <OtherPersonalHistory @form={{mh.form}} />
      <FamilyHistory @form={{mh.form}} />
      <LifeStyle @form={{mh.form}} @dateFormatter={{this.formatter}} />

      <mh.ComorbiditiesAllergiesSection />
      <mh.VaccinesSection @vaccines={{this.vaccines}} />
      <mh.CommentsSection />
    </MedicalHistoryBaseEditionForm>
  </template>
}
